<script lang="ts" setup>
import { useMate } from "@/stores/mate";
import { useMateDispatchMessage } from "@/stores/mateDispatchMessage";
import { useMission } from "@/stores/mission";
import { MessageListInstance } from "@/types/component";
import { Mate, MateDispatch } from "@/types/entity";
import { useRouteParams } from "@vueuse/router";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ThreadsNav from "../../ThreadsNav.vue";
import ClassicMissionMessageList from "./ClassicMissionMessageList.vue";
import { GateStatus } from "@/enums/mission";

// 状态
const routeName = "mateChatScene";
const mateStore = useMate();
const route = useRoute();
const router = useRouter();
const missionStore = useMission();
const dispatchMessageStore = useMateDispatchMessage();
const dispatchUuid = useRouteParams("dispatchUuid", "");
const targetMate = ref<Mate | null>(null);
const isLoading = ref(false);
const currentMateDispatch = ref<MateDispatch | null>(null);
const isPrivate = ref(true);
const myMessage = ref<string>("");
const isStreamReplying = ref(false);
const streamMsg = ref<string>("");
const isPostingMsg = ref<"start" | "end" | false>(false);
const myMsgToNames = ref<string[]>([]);
const messageListRef = ref<MessageListInstance | null>(null);
const headMessage = computed(() => {
  const msgList = dispatchMessageStore.messageListByDispatchUuid(
    dispatchUuid.value
  );
  return msgList?.[0] ?? null;
});

const sanEmoji = computed(() => {
  if (!currentMateDispatch.value?.san) {
    return "😐";
  }
  if (currentMateDispatch.value?.san > 8) {
    return "😊";
  }
  if (currentMateDispatch.value?.san > 5) {
    return "😐";
  }
  if (currentMateDispatch.value?.san > 2) {
    return "😨";
  }
  return "😱";
});
// Lifecycle
const prepareData = async () => {
  isLoading.value = true;
  try {
    currentMateDispatch.value = await missionStore.getMateDispatch(
      dispatchUuid.value
    );
    // 1. 获取聊天记录
    const messageListPromise = dispatchMessageStore.initMessageList(
      dispatchUuid.value
    );
    // 2. 获取聊天对象信息
    const targetMatePromise = mateStore.getOrFetchMate(
      currentMateDispatch.value.mate!.uuid
    );

    const [mate] = await Promise.all([targetMatePromise, messageListPromise]);
    targetMate.value = mate as unknown as Mate;
  } finally {
    isLoading.value = false;
  }
};
onBeforeMount(prepareData);
watch([dispatchUuid], () => {
  if (route.name === routeName) {
    prepareData();
  }
});

watch(headMessage, (head) => {
  if (!head || !currentMateDispatch.value) return;
  if (typeof head.dispatch.san === "number")
    currentMateDispatch.value.san = head.dispatch.san;
  if (typeof head.dispatch.hp === "number")
    currentMateDispatch.value.hp = head.dispatch.hp;
  if (head.dispatch.gateStatus != null)
    currentMateDispatch.value.gateStatus = head.dispatch.gateStatus;
});
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <ThreadsNav :show-left-logo="false" :show-center="false" class="py-2 pb-2">
      <template #left>
        <div class="flex items-center gap-1">
          <button class="btn btn-ghost btn-square" @click.stop="router.back()">
            <light-arrow-icon class="text-base" />
          </button>
          <div class="flex items-center gap-2">
            <UserAvatar
              v-if="targetMate"
              :avatar-url="targetMate.mediaUrl"
              class="h-9 lg:h-10"
            />
            <div class="flex flex-col border py-1 rounded-md bg-base-200 px-2">
              <div
                class="flex items-center gap-2"
                v-if="typeof currentMateDispatch?.hp === 'number'"
              >
                <span>❤️</span>
                <div class="relative flex items-center">
                  <progress
                    class="progress progress-success w-[100px]"
                    :value="currentMateDispatch?.hp"
                    :max="10"
                  />
                  <span
                    class="absolute inset-0 text-white text-xs text-center scale-75 -top-[3px]"
                  >
                    {{ currentMateDispatch?.hp }} / 10
                  </span>
                </div>
              </div>
              <div
                class="flex items-center gap-2"
                v-if="typeof currentMateDispatch?.san === 'number'"
              >
                <span>
                  {{ sanEmoji }}
                </span>
                <div class="relative flex items-center">
                  <progress
                    class="progress progress-warning w-[100px]"
                    :value="currentMateDispatch?.san"
                    :max="10"
                  />
                  <span
                    class="absolute inset-0 text-white text-xs text-center scale-75 -top-[4px]"
                  >
                    {{ currentMateDispatch?.san }} / 10
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #right>
        <div class="flex items-center gap-2 border py-1 px-2 rounded-full">
          <span
            class="rounded-full w-2 h-2"
            :class="
              currentMateDispatch?.gateStatus === GateStatus.Silent
                ? 'bg-red-500'
                : 'bg-green-500'
            "
          />
          <span class="text-xs">{{
            currentMateDispatch?.gateStatus === GateStatus.Silent
              ? "离线"
              : "在线"
          }}</span>
        </div>
      </template>
    </ThreadsNav>

    <!-- 内容区 -->
    <div
      v-if="isLoading"
      class="w-full h-full flex items-center justify-center"
    >
      <span class="loading loading-sm"></span>
    </div>
    <div v-else class="h-full grid grid-rows-[1fr_auto_auto] overflow-hidden">
      <ClassicMissionMessageList
        ref="messageListRef"
        :uuid="dispatchUuid"
        :mate="targetMate!"
        :my-message="myMessage"
        :stream-msg="streamMsg"
        :is-stream-replying="isStreamReplying"
        :is-posting-msg="isPostingMsg"
        :enable-reactive="false"
        :enable-tool="false"
      />

      <MissionTextFooter
        :dispatch="currentMateDispatch!"
        :enable-avatar="true"
        v-model:is-posting-msg="isPostingMsg"
        v-model:is-stream-replying="isStreamReplying"
        v-model:my-message="myMessage"
        v-model:stream-msg="streamMsg"
        :currentDispatch="currentMateDispatch"
        :enable-history-mode="false"
        :is-private="isPrivate"
        :mate="targetMate!"
        @onInputFocus="messageListRef?.scrollToBottom(false)"
        @on-post-msg-click="myMsgToNames = $event ?? []"
      />
    </div>
  </div>
</template>
<style scoped>
.chat-mode-toggle {
  --tw-border-opacity: 1;
  --tw-bg-opacity: 1;
}
</style>
